import React from 'react';
import { twMerge } from 'tailwind-merge';

type BadgeSize = 'sm' | 'md';
type BadgeColor = 'primary' | 'success' | 'error' | 'warning' | 'info' | 'gray';

interface BadgeProps {
  size?: BadgeSize; // Defaults to sm
  color?: BadgeColor; // Badge color
  className?: string;
  startIcon?: React.ReactNode; // Icon at the start
  endIcon?: React.ReactNode; // Icon at the end
  title?: string; // Native tooltip — a short badge label whose full meaning is a sentence
  children: React.ReactNode; // Badge content
}

export const Badge: React.FC<BadgeProps> = ({
  color = 'primary',
  size = 'sm',
  className,
  startIcon,
  endIcon,
  title,
  children,
}) => {
  const baseStyles =
    'inline-flex items-center whitespace-nowrap px-2.5 py-0.5 justify-center gap-1 rounded-full font-medium';

  const sizeStyles = {
    sm: 'text-xs',
    md: 'text-sm',
  };

  const colors: Record<BadgeColor, string> = {
    primary: 'bg-brand-50 text-brand-600 dark:bg-brand-500/15 dark:text-brand-400',
    success: 'bg-success-50 text-success-700 dark:bg-success-500/15 dark:text-success-500',
    error: 'bg-error-50 text-error-700 dark:bg-error-500/15 dark:text-error-500',
    warning: 'bg-warning-50 text-warning-700 dark:bg-warning-500/15 dark:text-warning-400',
    info: 'bg-blue-light-50 text-blue-light-700 dark:bg-blue-light-500/15 dark:text-blue-light-500',
    gray: 'bg-gray-100 text-gray-600 dark:bg-gray-700/15 dark:text-gray-400',
  };

  const sizeClass = sizeStyles[size];

  return (
    <span className={twMerge(baseStyles, sizeClass, colors[color], className)} title={title}>
      {startIcon && <span className="mr-1">{startIcon}</span>}
      {children}
      {endIcon && <span className="ml-1">{endIcon}</span>}
    </span>
  );
};
